import { randomUUID } from "#/shared/utils/string";
import { onMounted, ref } from "vue";
import { PaymentError, PaymentErrorCode } from "#ce/components/SpPayment/lib/error";

const stripeSDKUrl = "https://js.stripe.com/v3/";

// How long a validation waits for the Stripe SDK to load and the element to report ready. Neither
// `ready` nor `loaderror` fires while the Stripe script or iframe is still loading, so a stalled
// load would otherwise hold the validation forever.
const ELEMENT_READY_TIMEOUT = 30 * 1000;

/**
 * Stripe payment composable provides methods to interact with the Stripe SDK.
 *
 * @param {Object} config
 * @param {String} config.publishableKey - Stripe api key
 *
 * @throws {Error} - If publishableKey is not provided
 */
export function useStripe(host, config) {
  const { publishableKey } = config;

  const shadowHosts = ref([]);
  const mountId = ref();
  const mountPoint = ref();
  const slotName = ref(config.slotName ?? `mount-${randomUUID()}`);
  const elements = ref();
  const elementsClientSecret = ref();
  const paymentType = ref();

  // Resolves once the latest element is mounted and Stripe reports it ready. Rejects when the Stripe
  // SDK or the element fails to load.
  let elementReady;

  if (!publishableKey) {
    throw new Error("Stripe publishable key is required");
  }

  /**
   * Stripe instance
   * @type {import("vue").Ref<Stripe | null>}
   */
  const instance = ref(null);

  /**
   * Connect to the Stripe SDK
   *
   * We need to load the Stripe SDK before we can use it.
   * It is mandatory to fetch the SDK from the Stripe CDN in order to comply with PCI regulations.
   *
   * @returns {Promise<void>}
   */
  async function connect() {
    if (instance.value) {
      return;
    }

    if (window.Stripe === undefined) {
      await import(/* @vite-ignore */ stripeSDKUrl);
    }

    instance.value = new window.Stripe(publishableKey);
  }

  async function createElement(type, options, elementOptions) {
    if (!instance.value) {
      await connect();
    }

    // An Elements group is bound to the client secret it was built with, and Stripe offers no way to
    // repoint it, so a new secret needs a new group. Reusing the group across secrets would leave
    // `confirmPayment` charging the intent behind the *first* secret this element ever saw.
    if (!elements.value || elementsClientSecret.value !== options?.clientSecret) {
      initializeElements(options);
    }

    const element = elements.value.create(type, elementOptions);

    element.on("change", (event) => (paymentType.value = event.value.type));

    return element;
  }

  function initializeElements(options) {
    elements.value = instance.value.elements(options);
    elementsClientSecret.value = options?.clientSecret;
  }

  async function mountElement(element, { signal }) {
    const abortError = () => new Error(signal.reason);
    const abort = () => {
      throw abortError();
    };

    if (signal?.aborted) {
      abort();
    }

    const abortPromise = new Promise((_, reject) => {
      signal?.addEventListener("abort", () => reject(abortError()), { once: true });
    });

    if (!mountPoint.value) {
      await Promise.race([initMountPoint(), abortPromise]);
    }

    if (signal?.aborted) {
      abort();
    }

    element.mount(mountPoint.value);
  }

  async function createAndMountElement(type, options, { signal } = {}) {
    const elementOptions = {
      layout: {
        type: "accordion",
        radios: false,
      },
    };
    const mounting = createElement(type, options, elementOptions).then(async (element) => {
      const ready = waitForReady(element);
      await mountElement(element, { signal });
      return { element, ready };
    });

    elementReady = mounting.then(({ ready }) => ready);
    // Marks a load failure as handled. Only a validation waiting on `elementReady` reports it.
    elementReady.catch(() => {});

    const { element } = await mounting;

    return element;
  }

  function waitForReady(element) {
    return new Promise((resolve, reject) => {
      function handleLoadError(event) {
        reject(event.error);
      }

      element.on("ready", resolve);
      element.on("loaderror", handleLoadError);
    });
  }

  function createMountId() {
    const prefix = config?.mountPrefix ?? host.value.localName;
    return `${prefix}-mount-point-${randomUUID()}`;
  }

  function createSlot(slotName) {
    const node = document.createElement("slot");
    node.slot = slotName;
    node.name = slotName;
    return node;
  }

  function createMountPoint() {
    const node = document.createElement("div");
    node.id = mountId.value;
    node.classList.add(`${host.value.tagName.toLowerCase(0)}-mount-point`);
    return node;
  }

  /**
   * Initialize the mount point
   *
   * We traverse the shadow DOM tree to locate the root host element and establish a mount point.
   * This mount point is a div element intended to fill the slot defined within the custom element.
   * Since the mount point is created in the light DOM, it will be projected into the shadow DOM.
   */
  async function initMountPoint() {
    shadowHosts.value = [];
    let parentHost = host.value;

    // Traverse the shadow DOM tree to locate the root host element
    do {
      shadowHosts.value.push(parentHost);
      parentHost = parentHost.getRootNode().host;
    } while (parentHost !== undefined);

    const hosts = [...shadowHosts.value];
    const root = hosts.pop();
    if (!root.querySelector(`[slot="${slotName.value}"]`)) {
      const div = document.createElement("div");
      div.slot = slotName.value;
      root.appendChild(div);
    }

    // Create a mount point within the root host element
    const container = root.querySelector(`[slot="${slotName.value}"]`);
    container.appendChild(createMountPoint());

    // Create a slot within each shadow host element
    hosts.forEach((host) => {
      host.appendChild(createSlot(slotName.value));
    });

    mountPoint.value = document.getElementById(mountId.value);
  }

  async function confirmPayment({ returnUrl }) {
    const { error: validationError } = await validatePayment();

    if (validationError) {
      return { error: validationError };
    }

    const confirmParams = { return_url: returnUrl };

    const { error } = await instance.value.confirmPayment({
      elements: elements.value,
      confirmParams,
    });

    return {
      error,
    };
  }

  async function validatePayment() {
    if (!elementReady) {
      throw new PaymentError("The payment form was never created", undefined, PaymentErrorCode.ELEMENT_NOT_CREATED);
    }

    // A submit made before the element is ready leaves the form blank and never settles.
    await waitForElementReady();

    return elements.value.submit();
  }

  async function waitForElementReady() {
    let timeoutId;
    const timeout = new Promise((_, reject) => {
      function rejectWithTimeout() {
        reject(
          new PaymentError(
            "The payment form did not finish loading",
            undefined,
            PaymentErrorCode.ELEMENT_READY_TIMEOUT,
          ),
        );
      }

      timeoutId = setTimeout(rejectWithTimeout, ELEMENT_READY_TIMEOUT);
    });

    try {
      await Promise.race([elementReady, timeout]);
    } finally {
      clearTimeout(timeoutId);
    }
  }

  onMounted(async () => {
    mountId.value = createMountId();
  });

  return {
    // State
    paymentType,

    // Methods
    createAndMountElement,
    validatePayment,
    confirmPayment,
  };
}
